import { useLocation, useNavigate, useParams, useMatch } from 'react-router-dom';

/**
 * React Router v5-compatible history object
 */
interface History {
    push: (path: string, state?: any) => void;
    replace: (path: string, state?: any) => void;
    goBack: () => void;
    goForward: () => void;
    go: (n: number) => void;
    location: ReturnType<typeof useLocation>;
    action: 'PUSH' | 'REPLACE' | 'POP';
}

/**
 * React Router v5-compatible match object
 */
interface Match {
    params: Record<string, string>;
    isExact: boolean;
    path: string;
    url: string;
}

/**
 * Return type for useRouterProps hook
 */
export interface UseRouterPropsReturn {
    location: ReturnType<typeof useLocation>;
    history: History;
    match: Match;
    params: Record<string, string>;
}

/**
 * Hook that provides React Router v5-compatible props
 * Used for components still using withRouter or expecting v5 props
 * Can be gradually removed as components migrate to v6 hooks
 */
export const useRouterProps = (): UseRouterPropsReturn => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const match = useMatch(location.pathname);

    // Create v5-compatible history object
    const history: History = {
        push: (path: string, state?: any) => navigate(path, { state, replace: false }),
        replace: (path: string, state?: any) => navigate(path, { state, replace: true }),
        goBack: () => navigate(-1),
        goForward: () => navigate(1),
        go: (n: number) => navigate(n),
        location,
        action: 'PUSH',
    };

    // Create v5-compatible match object
    const matchProps: Match = match
        ? {
              params: params as Record<string, string>,
              isExact: match.pathname === location.pathname,
              path: match.pathnameBase || location.pathname,
              url: match.pathname || location.pathname,
          }
        : {
              params: params as Record<string, string>,
              isExact: true,
              path: location.pathname,
              url: location.pathname,
          };

    return {
        location,
        history,
        match: matchProps,
        params: params as Record<string, string>,
    };
};
